<template>
  <div id="afdc-stations">
    <template v-if="!browserSupported">
      <p class="afdc-alert afdc-alert-danger"><em v-html="browserSupportedMessage"></em></p>
    </template>
    <template v-else>
      <template v-if="$store.getters.getLoaderError">
        <p class="afdc-alert afdc-alert-warning"><em>{{ $store.getters.getLoaderError }}</em></p>
      </template>

      <template v-if="loading">
        <div class="afdc-large-spinner-container afdc-initial-loader">
          <div class="afdc-large-spinner"></div>
        </div>
      </template>
      <template v-else>
        <div id="afdc-modal-container" v-show="$store.getters.getEmbedModalOpen || $store.getters.getAboutModalOpen || $store.getters.getEvTermsModalOpen">
          <embed-form/>
          <about-the-data/>
          <ev-terms/>
        </div>

        <router-view/>
        <div v-if="!isMobileView">
          <footer-actions/>
        </div>
        <template v-if="!siteIsAfdcOrNrcan && !isMobileView">
          <widget-footer/>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import AboutTheData from '@/components/AboutTheData.vue';
import EvTerms from '@/components/EvTerms.vue';
import EmbedForm from '@/components/EmbedForm.vue';
import FooterActions from '@/components/FooterActions.vue';
import WidgetFooter from '@/components/WidgetFooter.vue';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-unminified';
import mapboxSupportedDetails from '@/lib/mapboxSupportedDetails';

export default {
  name: 'App',

  components: {
    EmbedForm,
    AboutTheData,
    EvTerms,
    FooterActions,
    WidgetFooter,
  },
  mounted() {
    if (window.afdcStationsOptions && window.afdcStationsOptions.kioskMode) {
      this.$el.classList.add('afdc-kiosk-mode');

      this.$el.addEventListener('click', () => {
        this.kioskResetReloadTimer();
      }, true);
    }

    if (window.afdcStationsOptions.localeCountry === 'CA' || window.afdcStationsOptions.corridor === false) {
      // hiding the corridor tool when displaying in canada
      this.$store.commit('setCorridorsPresent', false);
    }

    if ((window.afdcStationsOptions && window.afdcStationsOptions.mobile === true) || this.$route.query.mobile === 'true') {
      this.$store.commit('setMobileDisplay', true);
      this.$store.commit('setAlwaysMobile', true);
    }

    if (window.afdcStationsOptions.localeCountry === 'US') {
      this.$store.commit('setDisplayDirections', true);
    }
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    if (window.afdcStationsOptions.npsFunded === 'true') {
      this.$store.commit('setNpsStations', true);
      this.$router.push({ query: { ...this.$route.query, nps_funded: 'true' } });
    }

    // FIXME: A hard-coded workaround on Energy.gov pages to ensure our modal doesn't show up under
    // Energy.gov's header.
    //
    // A more proper fix would be to shift our modals to be injected directly into a child of
    // <body>, which would avoid stacking context issues, like this. This is how we've addressed
    // this in FCAB:
    // https://github.nrel.gov/TADA/afdc-sponge/blob/d0750e2056efa3be95ff35acd6c6809bc7df35b8/ui/battery-policies/src/embed.js#L47-L59
    // https://github.nrel.gov/TADA/afdc-stations/issues/241#issuecomment-41870
    //
    // But until we revisit more of this in this app, we're attempting to target some very specific
    // CSS rules on Energy.gov's page to alter the parent stacking context. This is prone to break,
    // but for now, this should hopefully fix the issue, while this CSS selector being so specific
    // that it's unlikely to trigger on any other non-Energy.gov embed.
    //
    // Also note that we inject this here with JavaScript, rather than in our normal CSS files to
    // avoid our postcss prefixing and namespacing config that otherwise would make it very
    // difficult to target these non-prefixed CSS classes that are global to the page.
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(`
      .body-inner.overlay-main .overlay-body .container .layout-content .content-wrapper {
        z-index: 12;
      }
    `));
    this.$el.appendChild(style);
  },

  computed: {
    browserSupported() {
      const supported = mapboxgl.supported();
      if (!supported) {
        this.$rollbar.warning('mapbox-gl is unuspported', mapboxSupportedDetails());
      }
      return supported;
    },

    browserSupportedMessage() {
      return this.$sprintf(this.$t('Your browser is not compatible with this application. Please update your browser to the latest version or <a href="%s">contact us</a> for assistance.'), 'https://afdc.energy.gov/website-contact.html');
    },

    loading() {
      return this.$store.getters.getLoadingInitialLoader;
    },

    siteIsAfdcOrNrcan() {
      const hostName = window.location.hostname;
      if (hostName.includes('afdc.energy.gov') || hostName.includes('natural-resources.canada.ca') || hostName.includes('ressources-naturelles.canada.ca') || hostName.includes('nrcan.gc.ca') || hostName.includes('rncan.gc.ca')) {
        return true;
      }
      return false;
    },

    isMobileView() {
      return this.$store.getters.isMobileView;
    },
  },

  watch: {
    loading: 'setLocale',

    '$store.getters.isMobileView': 'setMobileRoute',
  },

  methods: {
    setLocale() {
      const { locale } = this.$store.getters.loaderData;
      this.$store.commit('setLocale', locale);
      this.$_setLocale(locale);
    },

    kioskReload() {
      this.$router.push({ name: 'FindNearestStations', query: {} });
      this.$store.commit('setResetMapZoom', true);
    },

    kioskResetReloadTimer() {
      if (this.kioskReloadTimer) {
        clearTimeout(this.kioskReloadTimer);
      }

      this.kioskReloadTimer = setTimeout(this.kioskReload, 120000);
    },
    handleResize() {
      const alwaysMobile = this.$store.getters.isAlwaysMobile;
      const advancedOrCorridors = this.$store.getters.isNavActiveTabAnalyze || this.$store.getters.isNavActiveTabCorridors;
      if (!alwaysMobile && !advancedOrCorridors) {
        const appWidth = document.getElementById('afdc-stations').clientWidth;
        if (appWidth < 576) {
          this.$store.commit('setMobileDisplay', true);
          this.$store.commit('setLandscapeDisplay', false);
        } else if (appWidth >= 576 && appWidth < 768) {
          this.$store.commit('setLandscapeDisplay', true);
          this.$store.commit('setMobileDisplay', false);
        } else {
          this.$store.commit('setMobileDisplay', false);
          this.$store.commit('setLandscapeDisplay', false);
        }
      }
    },
    setMobileRoute() {
      const mobileRouteFilters = {
        activeTab: 'find',
        activeFindMode: 'nearest',
      };

      if (this.$store.getters.isMobileView) {
        this.$store.commit('setNavActive', mobileRouteFilters);
        this.$router.push({
          name: this.$store.getters.getNavActiveFilterRouteName,
          query: this.$store.getters.getFilters,
        });
      }
    },
  },
};
</script>

<style lang="scss">
$custom-control-indicator-bg: #fff;

@import 'src/assets/styles/cleanslate.scss';
@import '~bootstrap/scss/bootstrap.scss';
@import '~bootstrap/scss/bootstrap.scss';
@import 'mapbox-gl/dist/mapbox-gl';
@import 'animate.css/source/_base';
@import 'animate.css/source/sliding_entrances/slideInLeft';
@import 'animate.css/source/sliding_exits/slideOutLeft';
@import '~spinthatshit/src/loaders.scss';
@import '~flatpickr/dist/flatpickr';
@import '~tippy.js/index';

#modal-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);

  .modal-box {
    position: relative;
    overflow: auto;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: .3rem;
    outline: 0;
    padding: 20px;
    margin: 30px auto;

    .modal-closer {
      position: fixed;
      margin-top: -15px;
      color: #999;
      font-size: 25px;
      &:hover {
        color: #666;
        cursor: pointer;
      }
    }
  }
}

.large-spinner {
  @include loader02($size: 250px, $color: #666, $border-size: 25px);
}

.large-spinner-container {
  margin: 175px auto;
  position: absolute;
  left: calc(50% - 125px);
  z-index: 9;
}

.initial-loader {
  position: relative;
}

.animated.animated-fast {
  animation-duration: 0.3s;
}

#stations {
  // set styles from EERE app template for `body` element
  font-family: 'Karla', sans-serif;
  font-size: 16px;
  line-height: 1.65;
  color: #333;
  background-color: #fff;

  // Hide everything outside of the widget container, to ensure we don't spill
  // outside.
  overflow: hidden;
}

// override styles for linked `div` from EERE app template
a div:hover {
  color: inherit;
  background: none;
}

a.phone-link {
  float: left;
  margin-right: 5px;

  .fa-icon {
    margin: 4px 5px 0 0;
    float: left;
    color: inherit !important;
  }
}

// styles from the EERE app template
a {
  color: #337ab7;
}
a:hover {
  color: #23527c;
}

// The default Bootstrap success button colors do not meet our contrast requirements.
.btn-success {
  background-color: #007934;
  border-color: #007934;
  &:hover {
    background-color: #00672c;
    border-color: #00672c;
  }
}

.btn-route-directions {
  background-color: #555;
  border-color: #555;
  &:hover {
    background-color: #444;
    border-color: #444;
  }
}

// To meet contrast requirements for links on the default gray background, we need a darker link color.
a.dark-link {
  color: #23527c;
}
a.dark-link:hover {
  color: #15324b;
}

table.data {
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  font-size: .95em;
  margin: 1em 0;
  th, td {
    border-right: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    padding: 5px;
    line-height: 1.3em;
  }
  th {
    font-weight: bold;
  }
}

.background-gray-light {
  background-color: #eee;
}

.clear {
  clear: both;
}

#stations.kiosk-mode {
  a[href^="http"], a[href^="tel:"] {
    color: inherit !important;
    text-decoration: inherit !important;
    pointer-events: none !important;
  }

  .kiosk-mode-hide {
    display: none;
  }
}

 @media (max-width: 576px) {
  // for mobile - the nav is not sticky unless overflow is visible
  #stations {
    overflow: visible;
  }
 }

</style>
